/**
 * Datasets API
 */

import { apiClient } from "./apiClient";

export const datasetsAPI = {
  list: (organizationId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page);
    if (params.page_size) queryParams.append("page_size", params.page_size);
    if (params.search) queryParams.append("search", params.search);
    if (params.has_columns !== undefined)
      queryParams.append("has_columns", params.has_columns);

    const queryString = queryParams.toString();
    return apiClient.get(
      `/datasets/org/${organizationId}/${queryString ? "?" + queryString : ""}`,
    );
  },

  createWithColumns: (organizationId, payload) =>
    apiClient.post(`/datasets/org/${organizationId}/with-columns/`, payload),

  create: (organizationId, payload) =>
    apiClient.post(`/datasets/org/${organizationId}/`, payload),

  get: (organizationId, datasetId) =>
    apiClient.get(`/datasets/org/${organizationId}/${datasetId}/`),

  update: (organizationId, datasetId, payload) =>
    apiClient.patch(`/datasets/org/${organizationId}/${datasetId}/`, payload),

  delete: (organizationId, datasetId) =>
    apiClient.delete(`/datasets/org/${organizationId}/${datasetId}/`),

  listRows: (organizationId, datasetId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page);
    if (params.page_size) queryParams.append("page_size", params.page_size);
    if (params.search) queryParams.append("search", params.search);
    if (params.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params.sort_dir) queryParams.append("sort_dir", params.sort_dir);
    if (params.date_from) queryParams.append("date_from", params.date_from);
    if (params.date_to) queryParams.append("date_to", params.date_to);
    // Per-column filters: { filters: { colKey: val, colKey__gte: val, ... } }
    if (params.filters) {
      Object.entries(params.filters).forEach(([key, val]) => {
        if (val !== "" && val !== null && val !== undefined) {
          queryParams.append(`filter_${key}`, val);
        }
      });
    }

    const queryString = queryParams.toString();
    return apiClient.get(
      `/datasets/org/${organizationId}/${datasetId}/rows/${queryString ? "?" + queryString : ""}`,
    );
  },

  addRow: (organizationId, datasetId, payload) =>
    apiClient.post(
      `/datasets/org/${organizationId}/${datasetId}/rows/`,
      payload,
    ),

  updateRow: (organizationId, datasetId, rowId, payload) =>
    apiClient.patch(
      `/datasets/org/${organizationId}/${datasetId}/rows/${rowId}/`,
      payload,
    ),

  deleteRow: (organizationId, datasetId, rowId) =>
    apiClient.delete(
      `/datasets/org/${organizationId}/${datasetId}/rows/${rowId}/`,
    ),

  addColumn: (organizationId, datasetId, payload) =>
    apiClient.post(
      `/datasets/org/${organizationId}/${datasetId}/columns/`,
      payload,
    ),

  updateColumn: (organizationId, datasetId, columnId, payload) =>
    apiClient.patch(
      `/datasets/org/${organizationId}/${datasetId}/columns/${columnId}/`,
      payload,
    ),

  deleteColumn: (organizationId, datasetId, columnId) =>
    apiClient.delete(
      `/datasets/org/${organizationId}/${datasetId}/columns/${columnId}/`,
    ),

  bulkRows: (organizationId, payload) =>
    apiClient.post(`/datasets/org/${organizationId}/bulk-rows/`, payload),

  /**
   * Export rows in date range as CSV. Returns blob for download.
   * @param {number} organizationId
   * @param {{ date_from: string, date_to: string, dataset_id: number }} params - YYYY-MM-DD
   */
  exportCsv: (organizationId, params) => {
    const q = new URLSearchParams({
      date_from: params.date_from,
      date_to: params.date_to,
      dataset_id: String(params.dataset_id),
    });
    return apiClient.getBlob(
      `/datasets/org/${organizationId}/export/?${q.toString()}`,
    );
  },

  getSchema: (organizationId, datasetId) =>
    apiClient.get(`/datasets/org/${organizationId}/${datasetId}/schema/`),

  /**
   * Get distinct values for a column, with optional value from a second column.
   * @param {number} organizationId
   * @param {number} datasetId
   * @param {{ column_key: string, value_field?: string }} params
   */
  getColumnValues: (organizationId, datasetId, params = {}) => {
    const q = new URLSearchParams();
    if (params.column_key) q.append("column_key", params.column_key);
    if (params.value_field) q.append("value_field", params.value_field);
    return apiClient.get(
      `/datasets/org/${organizationId}/${datasetId}/column-values/?${q.toString()}`,
    );
  },

  generateChartData: (payload) =>
    apiClient.post(`/datasets/generate-chart-data/`, payload),
};
